import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faMapMarkerAlt,
  faVideo,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import { motion } from "framer-motion";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import Sidebar from "../../componentes/Sidebar";
import CountdownTimer from "../../componentes/CountdownTimer";
import { fetchEvents } from "../../api/events";
import { formatDate } from "../../utils/dateFormatter";
import PropTypes from "prop-types";
import { API_BASE_URL } from '../../api/config';
import MetaTags from "../../componentes/MetaTags";
import Pagination from "../../componentes/Pagination";

const baseUrl = API_BASE_URL;

// API fetch functions for news and alerts
const fetchNews = async (lang) => {
  // Add lang parameter
  const response = await fetch(`${baseUrl}/api/news?lang=${lang}`); // Include lang in query
  return response.json();
};

const fetchAlerts = async (lang) => {
  const response = await fetch(`${baseUrl}/api/alerts?lang=${lang}`);
  return response.json();
};

// PrimaryStatusButton and getStatusIcon (unchanged)
const PrimaryStatusButton = ({ isActive, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative px-3 py-2.5 rounded-md font-nunito-sans text-body transition-all duration-300 ${
      isActive
        ? "bg-primary text-white shadow-md"
        : "bg-white text-paragraph hover:bg-gray-200"
    }`}
  >
    <div className="relative flex items-center justify-center">
      {isActive && (
        <div className="absolute -top-1 -right-1 w-3 h-3">
          <div className="absolute w-3 h-3 bg-primary rounded-full animate-ping opacity-75"></div>
          <div className="absolute w-3 h-3 bg-primary rounded-full"></div>
        </div>
      )}
      <span>{label}</span>
    </div>
    {isActive && (
      <div className="absolute bottom-0 left-0 h-1 w-full bg-primary/30"></div>
    )}
  </button>
);

const getStatusIcon = (status) => {
  switch (status) {
    case "upcoming":
      return <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-600" />;
    case "ongoing":
      return (
        <div className="relative">
          <FontAwesomeIcon icon={faVideo} className="text-green-600" />
          <div className="absolute -top-1 -right-1 w-2 h-2">
            <div className="absolute w-2 h-2 bg-green-500 rounded-full animate-ping opacity-75"></div>
            <div className="absolute w-2 h-2 bg-green-500 rounded-full"></div>
          </div>
        </div>
      );
    case "completed":
      return <FontAwesomeIcon icon={faCheckCircle} className="text-gray-600" />;
    default:
      return <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-600" />;
  }
};

const Events = () => {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const lang = i18n.language;
  const [status, setStatus] = useState("");

  // Fetch events (main content)
  const { data, isLoading, error } = useQuery({
    queryKey: ["events", { status, page, lang }],
    queryFn: () => fetchEvents({ status, page, lang }),
  });

  // Fetch all events for status counts
  const { data: allEventsData, isLoading: isLoadingAll } = useQuery({
    queryKey: ["allEvents", { lang }],
    queryFn: async () => {
      let allResults = [];
      let currentPage = 1;
      let hasNext = true;
      while (hasNext) {
        const response = await fetchEvents({
          status: "",
          page: currentPage,
          lang,
        });
        allResults = [...allResults, ...response.results];
        hasNext = !!response.next;
        currentPage++;
      }
      return { results: allResults, count: allResults.length };
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  // Fetch news and alerts for sidebar using useQuery
  const { data: newsData, isLoading: newsLoading } = useQuery({
      queryKey: ["news", lang], // Add lang to queryKey
      queryFn: () => fetchNews(lang), // Pass lang to fetchNews
    });
  
    const {
      data: alertsData,
      isLoading: alertsLoading,
    } = useQuery({
      queryKey: ["alerts", lang], // Add lang to queryKey
      queryFn: () => fetchAlerts(lang), // Pass lang to fetchEvents
    });
  

  const news = newsData?.results || newsData || [];
  const alerts = alertsData?.results || alertsData || [];
  const loadingSidebar = newsLoading || alertsLoading;

  const events = data?.results || [];
  const totalPages = Math.ceil((data?.count || 0) / 5);

  const handleStatusChange = (value) => {
    setStatus(value);
    setPage(1);
  };

  const calculateStatusCounts = (eventsData) => {
    const counts = {
      all: eventsData?.count || 0,
      upcoming: 0,
      ongoing: 0,
      completed: 0,
    };
    (eventsData?.results || []).forEach((event) => {
      if (event.status && counts[event.status] !== undefined) {
        counts[event.status]++;
      }
    });
    return counts;
  };

  const statusCounts = allEventsData
    ? calculateStatusCounts(allEventsData)
    : { all: 0, upcoming: 0, ongoing: 0, completed: 0 };

  if (isLoading || isLoadingAll) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("events.title")}
          description={t("events.subtitle")}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("events.title"), link: null },
          ]}
          backgroundIcons={[faCalendarAlt, faMapMarkerAlt, faVideo]}
        />
        <FullscreenSpinner />
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("events.title")}
          description={t("events.subtitle")}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("events.title"), link: null },
          ]}
          backgroundIcons={[faCalendarAlt, faMapMarkerAlt, faVideo]}
        />
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-red-500 font-nunito-sans text-lg">
            {t("events.error", { message: error.message })}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <MetaTags title="Events — Ethio-CERT" description="Upcoming and past events by Ethio-CERT." url="https://ethiocert.gov.et/events" />
      <PageHeader />
      <GradientBanner
        title={t("events.title")}
        description={t("events.subtitle")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("events.title"), link: null },
        ]}
        backgroundIcons={[faCalendarAlt, faMapMarkerAlt, faVideo]}
      />
      <div className="min-h-screen">
        <div className="flex flex-col max-xl:px-4 py-4 md:py-8 px-dynamic">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-2 xl:gap-6 mt-6">
            <div className="md:col-span-2">
              <div className="flex flex-col gap-6 mb-6">
                <div className="w-full">
                  <div className="flex flex-wrap gap-2">
                    <PrimaryStatusButton
                      isActive={status === ""}
                      onClick={() => handleStatusChange("")}
                      label={t("events.all_status")}
                    />
                    <PrimaryStatusButton
                      isActive={status === "upcoming"}
                      onClick={() => handleStatusChange("upcoming")}
                      label={t("events.status.upcoming")}
                    />
                    <PrimaryStatusButton
                      isActive={status === "ongoing"}
                      onClick={() => handleStatusChange("ongoing")}
                      label={t("events.status.ongoing")}
                    />
                    <PrimaryStatusButton
                      isActive={status === "completed"}
                      onClick={() => handleStatusChange("completed")}
                      label={t("events.status.completed")}
                    />
                  </div>
                </div>
              </div>

              {events.length > 0 ? (
                <div className="flex flex-col gap-6">
                  {events.map((event) => (
                    <Link
                      key={event.id}
                      to={`/events/${event.slug}`}
                      className="flex flex-1 group hover:no-underline"
                    >
                      <div className="relative flex flex-col sm:flex-row h-auto min-h-[10rem] border border-gray-200 w-full rounded-tr-3xl rounded-l-md hover:border-primary transition-colors duration-300">
                        <div
                          className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-nunito-sans font-medium ${
                            event.status === "upcoming"
                              ? "bg-blue-100 text-blue-800"
                              : event.status === "ongoing"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {t(`events.status.${event.status}`)}
                        </div>
                        {event.image ? (
                          <div className="w-2/5 aspect-[2/1] max-sm:w-full flex-shrink-0 overflow-hidden rounded-md">
                            <img
                              src={event.image}
                              alt={event.title}
                              className="w-full h-full object-cover rounded-lg group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        ) : (
                          <div className="w-2/5 aspect-[2/1] max-sm:w-full flex-shrink-0 bg-gray-100 flex items-center justify-center">
                            <FontAwesomeIcon
                              icon={event.is_virtual ? faVideo : faCalendarAlt}
                              className="text-gray-400 text-4xl"
                            />
                          </div>
                        )}
                        <div className="flex flex-col gap-3 p-6 sm:ml-10 w-full">
                          <h1 className="font-catamaran text-h-6 font-bold lg:w-4/5 group-hover:text-primary transition-colors duration-300">
                            {event.title || "No Title"}
                          </h1>
                          <div className="space-y-4 text-gray-600">
                            <div className="flex items-start gap-4">
                              <FontAwesomeIcon
                                icon={faCalendarAlt}
                                className="text-primary mt-1.5 w-5 flex-shrink-0"
                              />
                              <div className="space-y-2">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                                  <span className="font-nunito-sans text-sm font-medium text-gray-700">
                                    {t("events.start_date")}:
                                  </span>
                                  <span className="font-nunito-sans text-sm">
                                    {formatDate(
                                      event.start_date,
                                      i18n.language
                                    )}
                                  </span>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                                  <span className="font-nunito-sans text-sm font-medium text-gray-700">
                                    {t("events.end_date")}:
                                  </span>
                                  <span className="font-nunito-sans text-sm">
                                    {formatDate(event.end_date, i18n.language)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <FontAwesomeIcon
                                icon={
                                  event.is_virtual ? faVideo : faMapMarkerAlt
                                }
                                className="text-primary w-5 flex-shrink-0"
                              />
                              <span className="font-nunito-sans text-sm text-gray-700">
                                {event.is_virtual
                                  ? t("events.virtual_event")
                                  : event.location}
                              </span>
                            </div>
                            {event.status === "upcoming" && (
                              <div className="mt-4">
                                <CountdownTimer targetDate={event.start_date} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <p className="font-nunito-sans text-lg text-gray-500">
                    {t("events.no_events")}
                  </p>
                </div>
              )}

              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                previousLabel={t("events.pagination.previous")}
                nextLabel={t("events.pagination.next")}
              />
            </div>

            <Sidebar
              primaryItems={news}
              secondaryItems={alerts}
              counts={statusCounts}
              loading={loadingSidebar}
              primaryTitle="news"
              secondaryTitle="alerts"
              countTitle="events.title"
              getCountIcon={getStatusIcon}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Events;

<style jsx global>{`
  @keyframes shimmer {
    0% {
      transform: translateX(-150%) skewX(-12deg);
    }
    100% {
      transform: translateX(150%) skewX(-12deg);
    }
  }
  .animate-shimmer {
    animation: shimmer 2s infinite;
  }
  @keyframes ping {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
  }
  .animate-ping {
    animation: ping 1.5s infinite;
  }
`}</style>;
